import { z } from "zod";

export const NOTIFICATION_TYPES = ["email", "push"] as const;
export const SCHEDULE_TYPES = ["one_time", "recurring"] as const;
export const DAYS_OF_WEEK = [
  "monday",
  "tuesday",
  "wednesday",
  "thursday",
  "friday",
  "saturday",
  "sunday",
] as const;

export const parseEmails = (text?: string) =>
  (text ?? "")
    .split(/[\s,;]+/)
    .map((email) => email.trim())
    .filter(Boolean);

const emailValidator = z.string().email();

export const notificationSchema = () =>
  z
    .object({
      notification_type: z.enum(NOTIFICATION_TYPES),
      agreement: z.number().nullable().optional(),
      emails_text: z.string().optional(),
      email_subject: z.string().optional(),
      email_body: z.string().optional(),
      push_subject: z.string().optional(),
      push_body: z.string().optional(),
      schedule_type: z.enum(SCHEDULE_TYPES),
      date: z.string().optional(),
      time: z.string().optional(),
      days_of_week: z.array(z.string()).optional(),
    })
    .superRefine((data, ctx) => {
      const required = (path: keyof typeof data, message = "Campo obrigatório") => {
        const value = data[path];
        if (!value || (Array.isArray(value) && !value.length)) {
          ctx.addIssue({ code: z.ZodIssueCode.custom, path: [path], message });
        }
      };

      if (data.notification_type === "email") {
        const emails = parseEmails(data.emails_text);
        const invalid = emails.filter((email) => !emailValidator.safeParse(email).success);
        if (!emails.length) required("emails_text", "Informe ao menos um e-mail");
        if (invalid.length) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["emails_text"],
            message: `E-mails inválidos: ${invalid.slice(0, 5).join(", ")}`,
          });
        }
        required("email_subject");
        // editor vazio ainda gera "<p></p>"
        if (!data.email_body?.replace(/<[^>]*>/g, "").trim()) {
          ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["email_body"], message: "Campo obrigatório" });
        }
      }

      if (data.notification_type === "push") {
        required("agreement", "Push é enviado para o app do convênio");
        required("push_subject");
        required("push_body");
      }

      if (data.schedule_type === "one_time") {
        required("date");
      } else {
        required("days_of_week", "Selecione ao menos um dia");
        required("time");
      }
    });

export type NotificationFormValues = z.infer<ReturnType<typeof notificationSchema>>;

/** Campos validados ao avançar cada etapa do formulário. */
export const STEP_FIELDS: Array<Array<keyof NotificationFormValues>> = [
  ["notification_type", "agreement", "emails_text"],
  ["email_subject", "email_body", "push_subject", "push_body"],
  ["schedule_type", "date", "time", "days_of_week"],
  [],
];

export function toNotificationPayload(values: NotificationFormValues) {
  const isEmail = values.notification_type === "email";
  const isOneTime = values.schedule_type === "one_time";

  return {
    notification_type: values.notification_type,
    schedule_type: values.schedule_type,
    agreement: values.agreement ?? null,
    active: true,
    // horário de Brasília sem offset: a API interpreta no TIME_ZONE dela (com ou sem USE_TZ)
    date: isOneTime && values.date ? `${values.date.slice(0, 16)}:00` : null,
    // recorrente: hora e dias no horário de Brasília (a API compara em horário local)
    time: !isOneTime ? values.time : null,
    days_of_week: !isOneTime ? values.days_of_week : [],
    ...(isEmail
      ? {
          emails: parseEmails(values.emails_text),
          email_subject: values.email_subject,
          email_title: values.email_subject,
          email_body: values.email_body,
        }
      : {
          push_subject: values.push_subject,
          push_body: values.push_body,
        }),
  };
}
